import { CKCompanyLogo, CKSearch, CKSwitch } from 'clearkit';
import { FC, useEffect, useState } from 'react';

import { GridList } from '~/components/unified/GridList';
import { SettingsCard } from '~/components/unified/SettingsCard';
import { Membership } from '~/generated/graphql';

import { useProfilePermissions } from './useProfilePermissions';

type NotificationTarget = 'user' | 'team';
type NotificationType =
  | 'usageEnabled'
  | 'billingEnabled'
  | 'adsManagementEnabled';

type FormNotificationProps = {
  data: any;
  onChange: (value: NotificationType) => void;
  type: NotificationTarget;
};

export const FormNotification: FC<FormNotificationProps> = ({
  data,
  onChange,
  type,
}) => {
  const [filter, setFilter] = useState('');
  const [filteredData, setFilteredData] = useState(data);

  const { isOwner } = useProfilePermissions();

  const isTeam = type === 'team';
  const isEditDisabled = !isOwner && isTeam;

  const handleChange = async (notificationType: NotificationType) => {
    onChange(notificationType);
  };

  useEffect(() => {
    const filtered = data.filter((datum: Membership) => {
      const email = datum.user.email?.toLowerCase();
      const name = datum.user.name?.toLowerCase() || '';
      const filterLowerCase = filter.toLowerCase();

      return email
        ? email.includes(filterLowerCase) || name.includes(filterLowerCase)
        : name.includes(filterLowerCase);
    });

    setFilteredData(filtered);
  }, [data, filter]);

  return (
    <SettingsCard>
      <SettingsCard.Header className="flex flex-wrap justify-between pb-6">
        <SettingsCard.Heading>
          {isTeam ? 'Team' : 'Your'} notification preferences
        </SettingsCard.Heading>
        <CKSearch
          className="w-72"
          onChange={setFilter}
          placeholder="Search members by name or email"
          value={filter}
        />
      </SettingsCard.Header>
      <SettingsCard.Body>
        <GridList
          className="text-sm grid gap-4"
          gridTemplateColumns="repeat(auto-fit, minmax(14rem, 1fr))"
        >
          <GridList.Header>
            <div>{type === 'user' ? 'Team' : 'Team member'}</div>
            <div>Usage notifications</div>
            <div>Ads management</div>
          </GridList.Header>

          {filteredData.length ? (
            filteredData.map((membership: Membership) => {
              const isUser = type === 'user';

              return (
                <GridList.Item key={membership.id}>
                  <div className="flex items-center gap-3">
                    {isUser ? (
                      <CKCompanyLogo domain={membership.account.domain ?? ''} />
                    ) : null}
                    <div>
                      <div className="font-medium">
                        {isUser
                          ? membership.account.name
                          : membership.user.name}
                      </div>
                      {isUser ? '' : membership.user.email}
                    </div>
                  </div>
                  <CKSwitch
                    checked={!!membership.notificationSettings?.usageEnabled}
                    id={`usage_${membership.id}`}
                    isDisabled={isEditDisabled}
                    onChange={() => handleChange('usageEnabled')}
                  >
                    <CKSwitch.LabelEnabled>On</CKSwitch.LabelEnabled>
                    <CKSwitch.LabelDisabled>Off</CKSwitch.LabelDisabled>
                  </CKSwitch>

                  <CKSwitch
                    checked={
                      !!membership.notificationSettings?.adsManagementEnabled
                    }
                    id={`ads_notification_${membership.id}`}
                    isDisabled={isEditDisabled}
                    onChange={() => handleChange('adsManagementEnabled')}
                  >
                    <CKSwitch.LabelEnabled>On</CKSwitch.LabelEnabled>
                    <CKSwitch.LabelDisabled>Off</CKSwitch.LabelDisabled>
                  </CKSwitch>
                </GridList.Item>
              );
            })
          ) : (
            <div className="py-6">
              No {isTeam ? 'team members' : 'teams'} found that match{' '}
              <strong>{filter}</strong>
            </div>
          )}
        </GridList>
      </SettingsCard.Body>
    </SettingsCard>
  );
};
